import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Grid,
  Typography,
  Button,
} from "@mui/material";
import Alert from "@mui/material/Alert"; // Import Alert for Snackbar
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const Edit = ({ open, onClose, siteDetail, onRecordUpdated }) => {
  // Renamed termDetail to siteDetail
  const { updateData, deleteData } = useFirestore("Sites"); // Specify collection
  const [formData, setFormData] = useState({
    id: "", // To store Firestore document ID
    SiteID: "",
    Coordinator_Site: 0,
    SiteName: "",
    SiteNameShort: "",
    SiteAddress: "",
    SiteCity: "",
    SiteState: "",
    SiteZip: "",
    SitePhone: "",
    SiteType: "",
    Notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (siteDetail) {
      setFormData({
        id: siteDetail.id || "",
        SiteID: siteDetail.SiteID || "",
        // Assuming siteDetail.Coordinator_Site is already -1 or 0 from Firestore
        Coordinator_Site: siteDetail.Coordinator_Site === -1 ? -1 : 0,
        SiteName: siteDetail.SiteName || "",
        SiteNameShort: siteDetail.SiteNameShort || "",
        SiteAddress: siteDetail.SiteAddress || "",
        SiteCity: siteDetail.SiteCity || "",
        SiteState: siteDetail.SiteState || "",
        SiteZip: siteDetail.SiteZip || "",
        SitePhone: siteDetail.SitePhone || "",
        SiteType: siteDetail.SiteType || "",
        Notes: siteDetail.Notes || "",
      });
    } else {
      // Reset form if siteDetail is null
      setFormData({
        id: "",
        SiteID: "",
        Coordinator_Site: 0,
        SiteName: "",
        SiteNameShort: "",
        SiteAddress: "",
        SiteCity: "",
        SiteState: "",
        SiteZip: "",
        SitePhone: "",
        SiteType: "",
        Notes: "",
      });
    }
  }, [siteDetail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? -1 : 0,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (!formData.id) {
        throw new Error("Document ID is missing for update.");
      }
      await updateData(formData.id, formData); // Pass ID and data separately
      setLoading(false);
      setSnackbarMessage("Record has been updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onRecordUpdated) onRecordUpdated();
      onClose(); // Close dialog on success
    } catch (error) {
      console.error("Error updating record:", error);
      setLoading(false);
      setSnackbarMessage(`Error updating record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (!siteDetail || !siteDetail.id) {
        throw new Error("Document ID is missing for delete.");
      }
      await deleteData(siteDetail.id);
      setLoading(false);
      setSnackbarMessage("Record has been deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onRecordUpdated) onRecordUpdated();
      onClose(); // Close the main dialog
    } catch (error) {
      console.error("Error deleting record:", error);
      setLoading(false);
      setSnackbarMessage(`Error deleting record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleConfirmDeleteOpen = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = () => {
    handleConfirmDeleteClose();
    handleDelete();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Site
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Coordinator_Site === -1}
                    onChange={handleCheckboxChange}
                    name="Coordinator_Site"
                  />
                }
                label="Coordinator Site"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Site IRN"
                name="SiteID"
                value={formData.SiteID}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Site Name"
                name="SiteName"
                value={formData.SiteName}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Site Name Short"
                name="SiteNameShort"
                value={formData.SiteNameShort}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Site Address"
                name="SiteAddress"
                value={formData.SiteAddress}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Site City"
                name="SiteCity"
                value={formData.SiteCity}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Site State"
                name="SiteState"
                value={formData.SiteState}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Site Zip"
                name="SiteZip"
                value={formData.SiteZip}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Site Phone"
                name="SitePhone"
                value={formData.SitePhone}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Site Type"
                name="SiteType"
                value={formData.SiteType}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Notes"
                name="Notes"
                value={formData.Notes}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          onDelete={handleConfirmDeleteOpen}
          deleting={loading}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleConfirmDeleteClose}
        onConfirm={handleConfirmDelete}
        deleting={loading}
        message="Are you sure you want to delete this?"
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
